/*
 * ingredient-cards.js — drag-to-scroll for the homepage ingredient row.
 *
 * PURE ENHANCEMENT. The row is a plain `overflow-x-auto` track that already
 * scrolls by trackpad, touch, keyboard and (before we hid it) the scrollbar.
 * Delete this file and none of that changes; the only thing lost is the
 * ability to drag the row with a mouse.
 *
 * WHY IT EXISTS. Hiding the scrollbar took away the mouse user's only
 * affordance and their only easy way to scroll horizontally — a wheel-only
 * mouse otherwise needs shift+wheel, which nobody discovers. `cursor: grab`
 * becomes the replacement signal, and it is set FROM SCRIPT rather than in the
 * markup on purpose: a grab cursor painted by CSS would promise dragging even
 * when this file failed to load.
 *
 * MOUSE AND PEN ONLY. Touch already has native momentum scrolling that is
 * better than anything written here, and calling `preventDefault` on a touch
 * pointer is the standard way this pattern breaks mobile. Touch pointers are
 * ignored entirely and fall through to the browser.
 *
 * NO EASING, NO MOMENTUM. `scrollLeft` is written directly per pointermove.
 * An animation layer on top of a native scroller fights it and drifts.
 */

/*
 * Two CSS properties on the track actively fight a direct `scrollLeft` write,
 * and both are neutralised for the duration of a drag and restored on release:
 *
 *   scroll-behavior: smooth  (`scroll-smooth`)   — animates every assignment,
 *     so the row lags behind the cursor and never catches up.
 *   scroll-snap-type: x mandatory (`snap-mandatory`) — re-snaps mid-gesture,
 *     which reads as the row fighting back.
 *
 * Restoring the snap on release is what makes the row settle onto a card
 * instead of stopping wherever the mouse happened to be — the snap is doing
 * useful work, it just cannot do it while the pointer is down.
 */
const DRAG_SUPPRESSED = { scrollBehavior: 'auto', scrollSnapType: 'none' }

function initTrack(track) {
  if (track.dataset.dragScrollReady === 'true') return
  track.dataset.dragScrollReady = 'true'

  /*
   * Set from script, never from the markup — see the header. Ten cards
   * overflow every viewport this theme targets, so the row is always
   * draggable and the cursor is never a lie.
   */
  track.style.cursor = 'grab'

  let activePointer = null
  let originX = 0
  let originScrollLeft = 0

  function onPointerDown(event) {
    // Primary button only, and never touch — see the header.
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return
    if (event.button !== 0) return

    activePointer = event.pointerId
    originX = event.clientX
    originScrollLeft = track.scrollLeft

    /*
     * Suppresses the browser's native drag-select BEFORE it begins. Setting
     * `user-select` once the gesture is already under way does not cancel a
     * selection that has started, which is why the first build painted
     * highlight boxes across the card names. The track also carries
     * `select-none` in the markup; this handles the initiating event.
     *
     * Unreachable for touch pointers, where preventing the default would cost
     * native scrolling — which is the whole reason for the guards above.
     */
    event.preventDefault()

    track.style.scrollBehavior = DRAG_SUPPRESSED.scrollBehavior
    track.style.scrollSnapType = DRAG_SUPPRESSED.scrollSnapType
    track.style.cursor = 'grabbing'

    /*
     * Pointer capture keeps the gesture alive when the cursor leaves the
     * track — without it a fast drag ends the moment the pointer crosses the
     * section edge. Guarded because jsdom has no pointer capture at all.
     */
    track.setPointerCapture?.(activePointer)
  }

  function onPointerMove(event) {
    if (activePointer === null || event.pointerId !== activePointer) return

    /*
     * Suppresses the browser's own text/image drag. Safe here because this
     * branch is unreachable for touch pointers, which is where preventing the
     * default would cost native scrolling.
     */
    event.preventDefault()

    track.scrollLeft = originScrollLeft - (event.clientX - originX)
  }

  function onPointerEnd(event) {
    if (activePointer === null || event.pointerId !== activePointer) return

    if (track.hasPointerCapture?.(activePointer)) {
      track.releasePointerCapture?.(activePointer)
    }
    activePointer = null

    // Hand the row back to CSS: smooth scrolling and snapping resume.
    track.style.scrollBehavior = ''
    track.style.scrollSnapType = ''
    track.style.cursor = 'grab'
  }

  track.addEventListener('pointerdown', onPointerDown)
  track.addEventListener('pointermove', onPointerMove)
  track.addEventListener('pointerup', onPointerEnd)
  track.addEventListener('pointercancel', onPointerEnd)

  /*
   * The cards carry background images. A native image drag would otherwise
   * start mid-gesture and paint a ghost that follows the cursor.
   */
  track.addEventListener('dragstart', (event) => event.preventDefault())
}

function initAll() {
  for (const track of document.querySelectorAll('[data-ingredient-track]')) {
    initTrack(track)
  }
}

initAll()

/*
 * The theme editor rebuilds a section's markup in place, which replaces the
 * track element and loses its listeners. Re-initialising on load restores
 * them; the `dragScrollReady` flag makes a repeat call a no-op everywhere else.
 */
document.addEventListener('shopify:section:load', initAll)

export { initTrack, initAll }
